/**
 * "Show this at most once a day, per account" — shared by the community
 * guidelines and the Watcher briefing.
 *
 * Both used to gate on sessionStorage, which made "once" mean once per session.
 * A session ends whenever a tab closes or the OS evicts the backgrounded app,
 * which on mobile is routine, so both modals reappeared several times a day.
 * The acceptance stamp lives in localStorage instead, so it survives relaunches.
 *
 * Shared rather than copied into each modal: two independent implementations of
 * the same interval rule would drift, and a gate that is subtly different in one
 * place is exactly the kind of thing nobody notices until it nags someone.
 */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether this account accepted within the interval.
 *
 * Keyed by uid so a shared device cannot accept on another account's behalf.
 */
export function acceptedRecently(prefix: string, uid: string, intervalMs: number = DAY_MS): boolean {
    if (!uid) return false;
    try {
        const at = Number(localStorage.getItem(`${prefix}${uid}`));
        if (!Number.isFinite(at) || at <= 0) return false;
        const age = Date.now() - at;
        // A negative age means the stamp is in the future — a device clock that
        // was wrong and has since been corrected. Treat that as no acceptance
        // rather than suppressing the modal until the clock catches up, which
        // could be indefinitely.
        return age >= 0 && age < intervalMs;
    } catch {
        // Safari in private mode throws on storage access. Showing the modal is
        // the safe direction to fail in.
        return false;
    }
}

/**
 * Whether this account has ever accepted, with no expiry.
 *
 * For a modal that should appear once and then never again on its own. Device
 * local, so it cannot answer for a fresh install or another browser — pair it
 * with a flag on the user document when "once per account" is what is meant,
 * and treat this as the fast path that avoids a flash before the profile loads.
 */
export function hasEverAccepted(prefix: string, uid: string): boolean {
    if (!uid) return false;
    try {
        return !!localStorage.getItem(`${prefix}${uid}`);
    } catch {
        return false;
    }
}

/**
 * Stamp acceptance at this moment.
 *
 * Called on accept rather than on show: someone who force-quits without
 * accepting has not agreed to anything, and should be asked again.
 */
export function stampAccepted(prefix: string, uid: string): void {
    if (!uid) return;
    try {
        localStorage.setItem(`${prefix}${uid}`, String(Date.now()));
    } catch {
        // Nothing to fall back to; the modal simply shows again next launch.
    }
}
